import * as React from "react";
import { GetKipsBasedOnGoalType } from "source/app/domain/Kips";
import { IGoalCardsState } from "source/app/presentation/MainPanel/Goals/GoalCards/Helpers/IGoalCardsState";
import GoalsContainer from "source/app/presentation/MainPanel/Goals/GoalsContainer";
import { EEventType } from "source/app/services/Events/EEventType";
import {
    DispatchEvent
} from "source/app/services/Events/Events";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import IsNotEqual from "source/app/services/Shared/ReactHelpers";
import { State } from "source/app/services/UI/State/State";
import {
    ILogEntry,
    resourceNameLogEntry,
} from "../../../../../../motw_core/Entity/ILogEntry";
import {
    LoadComponentStateAsync,
    SaveComponentStateAsync,
} from "../../../infrastructure/Data/MotwIndexedDb";
import Data from "../../../services/Data";
import Nameof from "../../../services/NameOf";
import { ESharedComponent } from "../../../services/UI/State/ESharedComponent";
import { IDiversityState } from "../../Header/Tools/Diversity/Diversity";
import { IGameIdentityScoresState } from "../GameIdentities/GameIdentityScores";
import { IGoalsContainerState } from "../Goals/IGoalsState";
import { GetGoalNamesLoggedToday } from "./GetGoalNamesLoggedToday";
import { HandleLogEntryEdit } from "./HandleLogEntryEdit";
import { ILogProps } from "./ILogProps";
import { ILogState } from "./ILogState";
import { LogInputForm } from "./LogInputForm/LogInputForm";
import { defaultLogInputValuesState } from "./LogInputForm/LogInputFormHelpers";
import { SetLogNavBadge } from "./SetLogNavBadge";
import { LogEntries } from "./LogEntries/LogEntries";
import ChevronPanel from "../../Shared/ChevronPanel/ChevronPanel";
import * as styles from "./Log.css";
import { ERPGIcon } from "motw_core/UI/Icons/ERPGIcon";

let passGoalNamesLoggedTodayTimeoutId: number | undefined;
let isLogMounted = false;

class Log extends React.Component<ILogProps, ILogState> {
    constructor(props: ILogProps) {
        super(props);
        this.state = {
            logData: [],
            goalNames: [],
            ...defaultLogInputValuesState,
            showEditIcon: false,
            dataHasChanged: false,
        };
    }

    async componentDidMount() {
        isLogMounted = true;
        Data.Load.Component({
            component: this,
            load: () =>
                ClientDataProvider.GetRecordsAsync(
                    `${resourceNameLogEntry}?order=lastmodifiedtime.desc&limit=64`
                ),
            stateTarget: Nameof<ILogState>("logData"),
            inLoadAll: true,
        });

        await LoadComponentStateAsync(this);

        // Goal search/selection is transient. Restoring it from IndexedDB can
        // hide every goal card after a reload until the user clears the input.
        this.setState({
            LogInpEntryText: defaultLogInputValuesState.LogInpEntryText,
            clickedGoalId: defaultLogInputValuesState.clickedGoalId,
        });

        PassGoalNamesLoggedToday(this.state.logData);
        State.ShareClass(ESharedComponent.Log, this);
    }

    componentWillUnmount() {
        isLogMounted = false;
        Data.Unload(this);

        if (passGoalNamesLoggedTodayTimeoutId !== undefined) {
            window.clearTimeout(passGoalNamesLoggedTodayTimeoutId);
            passGoalNamesLoggedTodayTimeoutId = undefined;
        }
    }

    async componentDidUpdate(prevProps: ILogProps, prevState: ILogState) {
        this.PredictAndUpdateKipsForEntry(
            prevState.LogInpEntryText,
            this.state.LogInpEntryText
        );

        SetLogNavBadge(this.state.logData);
        PassGoalNamesLoggedToday(this.state.logData);
        DispatchEvent(EEventType.Log_LogData_Changed, this.state.logData);
        await SaveComponentStateAsync(this);
        State.ShareClass(ESharedComponent.Log, this);
    }

    // Required to prevent unncessary renders
    // when Data.Load.All() is called
    shouldComponentUpdate(nextProps: ILogProps, nextState: ILogState) {
        const result = (
            IsNotEqual(nextProps, this.props) ||
            IsNotEqual(nextState, this.state)
        );

        return result;
    }

    async PredictAndUpdateKipsForEntry(
        previousEntry: string,
        currentEntry: string
    ) {
        if (previousEntry !== currentEntry) {
            var kipsValue = GetKipsBasedOnGoalType(currentEntry);
            this.setState({ inpKips: kipsValue.toString() });
        }
    }

    render() {
        let { logData } = this.state;

        if (!logData || logData.length === 0) {
            return null;
        }

        return (
            <div id="pnlLogMain">
                <div className={styles.logInputChevron}>
                    <ChevronPanel
                        id="logInputFormChevronPanel"
                        label="Log entry"
                        icon={ERPGIcon["quill-ink"]}
                        style={{ width: "100%" }}
                        dynamicOpacity={true}
                    >
                        <LogInputForm logState={this.state} />
                    </ChevronPanel>
                </div>
                <GoalsContainer
                    searchText={this.state.LogInpEntryText}
                    clickedGoalId={this.state.clickedGoalId}
                />
                <LogEntries
                    data={logData}
                    HandleDataChanged={HandleLogEntryEdit}
                    whenClicked={() => this.setState({ showEditIcon: true })}
                    // showReloadIcon={this.state.dataHasChanged}
                />
            </div>
        );
    }
}

export default Log;

export function ClearLogForm() {
    const log = State.ReadClass<ILogState>(ESharedComponent.Log);

    if (log) {
        log.setState({
            inpSignificance: "Important",
            inpVerb: "Achieving",
            inpKips: "10",
            LogInpEntryText: "",
        });
    }
}

function PassGoalNamesLoggedToday(logEntries: ILogEntry[]) {
    if (!isLogMounted) {
        return;
    }

    const Action = () => {
        const goalNamesLoggedToday = GetGoalNamesLoggedToday(logEntries);

        const gameIdentityScores = State.ReadClass<IGameIdentityScoresState>(
            ESharedComponent.GameIdentityScores
        );

        if (gameIdentityScores) {
            gameIdentityScores.setState({ goalNamesLoggedToday });
        }

        const goalCards = State.ReadClass<IGoalCardsState>(
            ESharedComponent.GoalCards
        );

        if (goalCards) {
            goalCards.setState({ goalNamesLoggedToday });
        }

        const diversity = State.ReadClass<IDiversityState>(
            ESharedComponent.Diversity
        );

        if (diversity) {
            diversity.setState({ goalNamesLoggedToday });
        }
    };

    const goalsContainer = State.ReadClass<IGoalsContainerState>(
        ESharedComponent.GoalsContainer
    );

    if (!goalsContainer) {
        if (passGoalNamesLoggedTodayTimeoutId !== undefined) {
            window.clearTimeout(passGoalNamesLoggedTodayTimeoutId);
        }

        passGoalNamesLoggedTodayTimeoutId = window.setTimeout(
            () => PassGoalNamesLoggedToday(logEntries),
            2000
        );
    } else {
        if (passGoalNamesLoggedTodayTimeoutId !== undefined) {
            window.clearTimeout(passGoalNamesLoggedTodayTimeoutId);
            passGoalNamesLoggedTodayTimeoutId = undefined;
        }

        Action();
    }
}
